/*
  <variant-visual-picker>: the PDP flavor and size picker
  (snippets/variant-visual.liquid).

  The visible flavor and size radios are this component's own. They never submit and
  carry no data-variant-input, so the theme never reads them. On every change the
  component mirrors the choice into the hidden native radios (.variant-input-wrap),
  then dispatches one change event, and theme.Variants (assets/theme.js) resolves
  the variant, price, images and add-to-cart state exactly as before.

  When a flavor doesn't offer the selected size, the component picks another size
  first (the shopper's last chosen size if the flavor has it, otherwise Large single
  pack, otherwise the first available), hides the sizes the flavor lacks, then
  announces the switch politely. Focus never moves.

  Load order matters: theme.js (deferred in <head>) runs before this file and its
  default-size script (snippets/product-template.liquid) switches the native radios
  to Large before this element is defined. So on connect the component reads the
  native radios rather than trusting the server-rendered state.
*/
if (!customElements.get('variant-visual-picker')) {
  customElements.define(
    'variant-visual-picker',
    class VariantVisualPicker extends HTMLElement {
      connectedCallback() {
        this.variants = this.parseJson('[data-vvp-variants]', []);
        this.strings = this.parseJson('[data-vvp-strings]', {});
        this.flavorIndex = this.readIndex('flavorIndex');
        this.sizeIndex = this.readIndex('sizeIndex');
        this.qtyIndex = this.readIndex('qtyIndex');

        this.flavorInputs = Array.from(this.querySelectorAll('[data-vvp-flavor]'));
        this.sizeInputs = Array.from(this.querySelectorAll('[data-vvp-size]'));
        this.sizeCards = this.querySelector('.visual-picker__cards--size');
        this.callout = this.querySelector('[data-vvp-callout]');
        this.statusRegion = this.querySelector('[data-vvp-status]');
        this.nativeFieldsets = Array.from(this.querySelectorAll('.visual-picker__native-inputs .variant-input-wrap'));

        // The size to come back to when a flavor that has it is chosen again: the
        // shopper's own last pick, or until they make one, the page's current size
        // (so a ?variant= link to the Value size survives a look at other flavors).
        this.preferredSizeInput = null;
        this.userPickedSize = false;
        this.burst = null;
        this.settleTimer = null;
        this.clearTimer = null;

        this.onChange = this.onChange.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onResize = this.onResize.bind(this);

        this.addEventListener('change', this.onChange);
        document.addEventListener('variant:change', this.onVariantChange);
        window.addEventListener('resize', this.onResize);

        this.measureHeader();
        this.reconcileOnConnect();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
        document.removeEventListener('variant:change', this.onVariantChange);
        window.removeEventListener('resize', this.onResize);
        clearTimeout(this.settleTimer);
        clearTimeout(this.clearTimer);
        cancelAnimationFrame(this.resizeFrame);
      }

      /* ---------- Setup helpers ---------- */

      parseJson(selector, fallback) {
        const el = this.querySelector(selector);
        if (!el) return fallback;
        try {
          return JSON.parse(el.textContent);
        } catch (error) {
          return fallback;
        }
      }

      readIndex(key) {
        const value = this.dataset[key];
        return value === undefined || value === '' ? null : parseInt(value, 10);
      }

      // Keeps the focused card clear of the pinned header (scroll-margin in the CSS)
      measureHeader() {
        const header = document.getElementById('SiteHeader') || document.querySelector('.site-header');
        if (header && header.offsetHeight) {
          this.style.setProperty('--vvp-header-h', `${header.offsetHeight}px`);
        }
      }

      onResize() {
        cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = requestAnimationFrame(() => this.measureHeader());
      }

      // The theme may have changed the native radios before this element existed
      // (the default-size script), and a shopper on a slow connection may have
      // tapped a card before this script loaded. A tap wins; otherwise follow the
      // native radios. Nothing is announced either way.
      reconcileOnConnect() {
        const tappedSize = this.sizeInputs.find((el) => el.checked && !el.defaultChecked);
        const tappedFlavor = this.flavorInputs.find((el) => el.checked && !el.defaultChecked);

        if (tappedSize || tappedFlavor) {
          if (tappedSize) {
            this.preferredSizeInput = tappedSize;
            this.userPickedSize = true;
          }
          this.handleUserChange(Boolean(tappedFlavor), true);
          return;
        }
        this.applyOptions(this.nativeOptions());
      }

      nativeOptions() {
        return this.nativeFieldsets.map((fieldset) => {
          const checked = Array.from(fieldset.querySelectorAll('input[data-variant-input]')).find((el) => el.checked);
          return checked ? checked.value : null;
        });
      }

      /* ---------- State lookups ---------- */

      checkedFlavor() {
        const input = this.flavorInputs.find((el) => el.checked);
        return input ? input.value : null;
      }

      checkedSizeInput() {
        return this.sizeInputs.find((el) => el.checked) || null;
      }

      findVariant(flavor, sizeInput) {
        if (!sizeInput && this.sizeIndex !== null) return null;
        return (
          this.variants.find((variant) => {
            const options = variant.options;
            if (this.flavorIndex !== null && options[this.flavorIndex] !== flavor) return false;
            if (this.sizeIndex !== null && options[this.sizeIndex] !== sizeInput.dataset.sizeValue) return false;
            if (this.qtyIndex !== null && options[this.qtyIndex] !== sizeInput.dataset.qtyValue) return false;
            return true;
          }) || null
        );
      }

      // Size choices for a flavor, in display order, each with its variant
      offeredSizes(flavor) {
        return this.sizeInputs
          .map((input) => ({ input, variant: this.findVariant(flavor, input) }))
          .filter((entry) => entry.variant);
      }

      // The size to land on when the flavor changes: the shopper's own pick, then
      // where this burst of changes started (so a round trip lands back where it
      // began), then the current size, then Large single pack, then anything.
      targetSizeFor(flavor, currentInput) {
        const offered = this.offeredSizes(flavor);
        const has = (input) => input && offered.some((entry) => entry.input === input);

        if (has(this.preferredSizeInput)) return this.preferredSizeInput;
        if (this.burst && has(this.burst.startSize)) return this.burst.startSize;
        if (has(currentInput)) return currentInput;

        const available = offered.filter((entry) => entry.variant.available);
        const pick =
          available.find((entry) => entry.input.hasAttribute('data-default-candidate')) ||
          available[0] ||
          offered.find((entry) => entry.input.hasAttribute('data-default-candidate')) ||
          offered[0];
        return pick ? pick.input : currentInput;
      }

      /* ---------- User changes ---------- */

      onChange(event) {
        const input = event.target;
        if (input.matches('[data-vvp-flavor]')) {
          this.handleUserChange(true);
        } else if (input.matches('[data-vvp-size]')) {
          this.preferredSizeInput = input;
          this.userPickedSize = true;
          this.handleUserChange(false);
        }
        // Changes from the hidden native inputs are the theme's business.
      }

      handleUserChange(isFlavorChange, silent = false) {
        const flavor = this.checkedFlavor();
        const sizeBefore = this.checkedSizeInput();

        if (!silent) {
          if (!this.burst) this.burst = { startSize: sizeBefore };
          this.burst.lastChangeWasFlavor = isFlavorChange;
        }

        // 1. Settle the size first, so the checked radio is never one about to be
        //    hidden (that would drop the group from the Tab order).
        let sizeInput = sizeBefore;
        if (this.sizeIndex !== null && (isFlavorChange || !this.findVariant(flavor, sizeBefore))) {
          sizeInput = this.targetSizeFor(flavor, sizeBefore);
          if (sizeInput && !sizeInput.checked) sizeInput.checked = true;
        }

        // 2. Mirror into the native radios without events.
        const changedNative = this.writeNative(flavor, sizeInput);

        // 3. Hide what this flavor lacks, mark sold out, toggle the callout.
        this.refresh(flavor);

        // 4. One change event, so theme.Variants resolves the whole selection once.
        //    theme.Variants answers with variant:change synchronously; that echo of
        //    our own write is ignored (see onVariantChange).
        if (changedNative) {
          this.dispatching = true;
          try {
            changedNative.dispatchEvent(new Event('change', { bubbles: true }));
          } finally {
            this.dispatching = false;
          }
        }

        // 5. Announce once the shopper pauses.
        if (!silent) this.queueAnnouncement();
      }

      // Checks the native radios for the selection. Returns the last native input
      // that changed, or null if they already matched.
      writeNative(flavor, sizeInput) {
        let changed = null;
        const setNative = (index, value) => {
          if (index === null || value === null || value === undefined) return;
          const fieldset = this.nativeFieldsets[index];
          if (!fieldset) return;
          const input = Array.from(fieldset.querySelectorAll('input[data-variant-input]')).find(
            (el) => el.value === value
          );
          if (input && !input.checked) {
            input.checked = true;
            changed = input;
          }
        };

        setNative(this.flavorIndex, flavor);
        if (sizeInput) {
          setNative(this.sizeIndex, sizeInput.dataset.sizeValue);
          setNative(this.qtyIndex, sizeInput.dataset.qtyValue);
        }
        return changed;
      }

      /* ---------- Rendering ---------- */

      refresh(flavor) {
        let visible = 0;
        this.sizeInputs.forEach((input) => {
          const card = input.closest('.visual-picker__option');
          const variant = this.findVariant(flavor, input);
          // Never hide the checked radio; it keeps the group's Tab stop.
          const show = Boolean(variant) || input.checked;
          card.hidden = !show;
          if (show) visible += 1;

          const soldOut = card.querySelector('[data-vvp-sold-out]');
          if (soldOut) soldOut.hidden = !(variant && !variant.available);
        });

        if (this.sizeCards) this.sizeCards.dataset.count = String(visible);

        if (this.callout) {
          const selected = this.checkedSizeInput();
          this.callout.hidden = !(selected && selected.hasAttribute('data-value-size'));
        }
      }

      /* ---------- Outside changes (deep links, the default-size script) ---------- */

      onVariantChange(event) {
        if (this.dispatching) return;
        const variant = event.detail && event.detail.variant;
        if (!variant) return;
        if (!this.variants.some((candidate) => candidate.id === variant.id)) return;
        this.applyOptions(variant.options || [variant.option1, variant.option2, variant.option3]);
      }

      // Checks the visible radios to match a set of option values. Setting .checked
      // fires no events, so this never loops, and it never announces.
      applyOptions(options) {
        if (this.flavorIndex !== null) {
          const flavorInput = this.flavorInputs.find((el) => el.value === options[this.flavorIndex]);
          if (flavorInput) flavorInput.checked = true;
        }

        if (this.sizeIndex !== null) {
          const sizeInput = this.sizeInputs.find(
            (el) =>
              el.dataset.sizeValue === options[this.sizeIndex] &&
              (this.qtyIndex === null || el.dataset.qtyValue === options[this.qtyIndex])
          );
          if (sizeInput) {
            sizeInput.checked = true;
            if (!this.userPickedSize) this.preferredSizeInput = sizeInput;
          }
        }

        this.refresh(this.checkedFlavor());
      }

      /* ---------- Announcements ---------- */

      queueAnnouncement() {
        clearTimeout(this.settleTimer);
        clearTimeout(this.clearTimer);
        if (this.statusRegion) this.statusRegion.textContent = '';
        // Speaking in the same tick gets cut off by the focus announcement.
        this.settleTimer = setTimeout(() => this.announce(), 700);
      }

      announce() {
        const burst = this.burst;
        this.burst = null;
        // A size the shopper picked is read out as the focused radio's name
        // (including "Sold out"), so only flavor changes need a message.
        if (!burst || !burst.lastChangeWasFlavor || !this.statusRegion) return;

        const flavor = this.checkedFlavor();
        const settled = this.checkedSizeInput();
        if (!settled) return;

        const messages = [];
        const sizeChanged = settled !== burst.startSize;
        const wanted = this.preferredSizeInput || burst.startSize;

        if (sizeChanged && wanted && settled !== wanted && !this.findVariant(flavor, wanted)) {
          messages.push(
            this.format(this.strings.sizeSwitched, {
              old_size: wanted.dataset.label,
              flavor: flavor || '',
              new_size: settled.dataset.label,
            })
          );
        } else if (sizeChanged) {
          messages.push(this.format(this.strings.sizeSelected, { size: settled.dataset.label }));
        }

        // The sold-out card isn't the focused one after a flavor change, so say it.
        const variant = this.findVariant(flavor, settled);
        if (variant && !variant.available) {
          messages.push(this.format(this.strings.sizeSoldOut, { size: settled.dataset.label }));
        }

        if (!messages.length) return;
        this.statusRegion.textContent = messages.join(' ');
        this.clearTimer = setTimeout(() => {
          this.statusRegion.textContent = '';
        }, 8000);
      }

      format(template, values) {
        if (!template) return '';
        return Object.keys(values).reduce(
          (text, key) => text.split(`[${key}]`).join(values[key]),
          template
        );
      }
    }
  );
}
